import { useMutation } from '@apollo/client';
import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateControlGroupMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetControlGroupsFlatDocument,
  namedOperations,
  UpdateControlGroupDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateControlGroupTRPC } from './useUpdateControlGroupTRPC';

type UpdateControlGroupInput = {
  Id: string;
  Title: string;
  Description: string;
  Owner: string;
  OriginalTimestamp: string;
  CustomAttributeData?: CustomAttributeData | null;
};

export const useUpdateControlGroup = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [updateControlGroupGraphQL, graphqlState] = useMutation(
    UpdateControlGroupDocument,
    {
      update: (cache) => {
        evictField(cache, 'control_group');
        evictField(cache, 'control_group_aggregate');
      },
      refetchQueries: [
        namedOperations.Query.getControlGroupById,
        { query: GetControlGroupsFlatDocument },
      ],
      awaitRefetchQueries: true,
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateControlGroupTRPC();

  const updateControlGroup = async (
    variables: UpdateControlGroupInput
  ): Promise<UpdateControlGroupMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateControlGroup(variables);
    }

    const result = await updateControlGroupGraphQL({
      variables: {
        Id: variables.Id,
        Title: variables.Title,
        Description: variables.Description,
        Owner: variables.Owner,
        OriginalTimestamp: variables.OriginalTimestamp,
        CustomAttributeData: variables.CustomAttributeData ?? null,
      },
    });
    if (!result.data) {
      throw new Error('Failed to update control group');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateControlGroup,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateControlGroup,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
